import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";

import { StatusBadge } from "@/components/StatusBadge";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import { SavedViewChips } from "@/components/table/SavedViewChips";
import type { DataTableColumn, DataTableFilterConfig } from "@/components/table/columns";
import type { InlineEditCommitArgs, InlineEditConflictArgs } from "@/components/table/use-inline-edits";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, Role, UserResource } from "@/lib/api-types";
import { useIsOwner, useIsReadOnly } from "@/lib/current-actor";
import { ROLE_LABELS, USER_STATUS_LABELS } from "@/lib/enum-labels";
import { useEnums } from "@/lib/enums";
import { labelFor } from "@/lib/field-labels";
import { formatDateCell } from "@/lib/format";
import {
  conflictMessage,
  freshestKnownValue,
  type EditCommitOutcome,
  type EditCommitPayload,
} from "@/lib/inline-edit";
import {
  appendUnknownParams,
  buildListSearch,
  PAGE_PARAM,
  pageSizeFor,
  parseFilterParams,
  parsePageParam,
  parseSortParam,
  SORT_PARAM,
} from "@/lib/list-query";
import { useIsPhone } from "@/lib/use-media-query";

import { AddUserDialog } from "./AddUserDialog";

/** Story 2.2's Code Map: `role`/`status`, matching the backend's own
 * `FILTERABLE_FIELDS` (`app/domain/identity/administration.py`) exactly —
 * a filter this list offers that the API doesn't recognise would 400. */
const FILTERABLE_FIELDS = ["role", "status"] as const;

/** Story 2.4: this list's own identity, so a saved view created here never
 * appears on some future list's chip row (spec judgement call 3). */
const USERS_LIST_KEY = "users";

/**
 * The value half of `PATCH /users/{id}`'s body a commit produces — the
 * field name plus the raw string it should carry. `resolveEditCommit`
 * (`lib/inline-edit.ts`) always hands this route a `"value"`-kind payload
 * for both `display_name` and `role`: neither is a `"transition"`-kind
 * column (that shape is proven only synthetically, VC-2.3-18 — AD-23 does
 * not bind `identity`/`users`, judgement call 3).
 */
function patchBodyFor(columnKey: string, payload: EditCommitPayload): Record<string, unknown> {
  if (payload.kind !== "value") {
    throw new Error(`unexpected transition-kind payload for column "${columnKey}"`);
  }
  return { [columnKey]: payload.value };
}

/**
 * Turns a raw field *value* into what this screen already shows for it
 * everywhere else (Story 2.3 review round 2 — the conflict banner was the
 * last place a raw enum value like `read_only` still reached the screen
 * verbatim; the resting cell, the unsaved state and the error state were
 * already fixed in an earlier round). Only `role` is a fixed-option field
 * with raw values a person has never seen; `display_name` is free text a
 * person typed, already plain language, needing no relabeling — so this is
 * deliberately narrower than `field-labels.ts`'s `labelFor`, which names
 * *fields*, not *values*. Passed into `conflictMessage` (`lib/inline-edit.ts`)
 * so that generic function stays ignorant of `Role`/`ROLE_LABELS`, the same
 * "no route-specific branch" rule the rest of `DataTable` follows.
 */
function labelForConflictValue(field: string, rawValue: string): string {
  return field === "role" ? (ROLE_LABELS[rawValue as Role] ?? rawValue) : rawValue;
}

/**
 * `/settings/users` (spec judgement call 5) — Story 1.7's `GET /users`,
 * with a real screen in front of it for the first time. Owner-only
 * server-side; a Member/Read-only visiting this route gets the API's real
 * `403` rendered by `DataTable`'s error state (VC-2.2-21) — this component
 * does not pre-guess the role client-side and skip the fetch, matching
 * AD-14 ("the frontend mirrors route role declarations to decide what to
 * render and enforces nothing" — the *server's* refusal is what renders).
 */
export function UsersList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const enumsQuery = useEnums();
  const queryClient = useQueryClient();
  const isReadOnly = useIsReadOnly();
  const isOwner = useIsOwner();
  /** `null` means no create dialog is open — mirrors `VendorsList.tsx`'s own
   * `createOpen` state exactly (`POST /users` is `Role.OWNER`-gated the same
   * way `POST /vendors` is). */
  const [createOpen, setCreateOpen] = useState(false);
  /** Story 2.6's retrofit of the row action Story 2.2 deferred (its own
   * judgement call 3) waiting for this story's confirm-dialog pattern.
   * `null` means no confirm dialog is open. */
  const [removeTarget, setRemoveTarget] = useState<UserResource | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | undefined>(undefined);
  /** A stable fallback focus target for the confirm dialog: confirming a
   * removal unmounts the row (and its "Remove" trigger) as part of the
   * same action that closes the dialog, so the page's own heading — which
   * never goes away — is where focus lands instead of falling through to
   * `<body>` (found live; see `ConfirmDialog.tsx`'s own doc). */
  const headingRef = useRef<HTMLHeadingElement>(null);
  /**
   * Review finding 3: a second edit to a row whose first edit is still in
   * flight (or has just resolved but hasn't triggered a re-render yet) must
   * not source `expected_updated_at` from the `row` prop as it was when the
   * table last rendered — that value can already be stale by the time the
   * second edit's request is built, producing a `409` against the user's
   * *own* prior write. This ref is updated synchronously the instant each
   * commit's real response arrives (`writeRowToCache`, below), independent
   * of React's render cycle, and `use-inline-edits.ts`'s per-row commit
   * queue guarantees a second edit's request for the same row is never
   * built until the first one has already updated it.
   */
  const latestUpdatedAtRef = useRef<Record<string, string>>({});

  useEffect(() => {
    document.title = "Users — PropDesk";
  }, []);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const activeFilters = parseFilterParams(searchParams, FILTERABLE_FIELDS);
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  // Story 2.4, VC-2.4-10: a stale saved view can carry a filter field this
  // build's column config no longer recognises. `activeFilters` above only
  // ever holds known fields (right for deciding which filter chips to
  // render), so forwarding *only* those to the fetch would silently drop
  // the unrecognised one and show an unfiltered list — exactly the silent
  // fallback the spec forbids. `appendUnknownParams` carries anything else
  // present on the URL straight through, so the API's own grammar refusal
  // (`app/api/query.py`) is what the account holder actually sees.
  const search = appendUnknownParams(
    buildListSearch({ sort, filters: activeFilters, page, pageSize }),
    searchParams,
    FILTERABLE_FIELDS,
  );
  const listQueryKey = ["users", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<UserResource>>(`/users?${search}`),
    placeholderData: keepPreviousData,
  });

  // Only fetched when a filter is active and only to answer "how many rows
  // exist without it" (VC-2.2-20) — most sessions never trigger this
  // second request. `page_size=1` keeps its payload minimal; only
  // `meta.total` is read from it.
  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const unfilteredTotalQuery = useQuery({
    queryKey: ["users", "total"],
    queryFn: () => apiFetch<Page<UserResource>>("/users?page_size=1"),
    enabled: hasActiveFilters,
  });

  const filters = useMemo<DataTableFilterConfig[]>(() => {
    const enums = enumsQuery.data;
    if (!enums) {
      return [];
    }
    return [
      {
        field: "role",
        label: labelFor("role"),
        options: enums.roles.map((role) => ({ value: role, label: ROLE_LABELS[role] })),
      },
      {
        field: "status",
        label: labelFor("status"),
        options: enums.statuses.map((status) => ({ value: status, label: USER_STATUS_LABELS[status] })),
      },
    ];
  }, [enumsQuery.data]);

  /**
   * Writes a freshly-received `UserResource` into every cached list this
   * screen holds (`useQueriesData`'s partial-key match reaches
   * `["users","list",search]` for whatever `search` was active, plus
   * `["users","total"]`) — synchronously, before the commit that produced it
   * resolves, so the resting cell's next render reads the real new value
   * rather than a stale one the query hasn't refetched yet.
   */
  function writeRowToCache(updated: UserResource) {
    latestUpdatedAtRef.current[updated.id] = updated.updated_at;
    queryClient.setQueriesData<Page<UserResource>>({ queryKey: ["users"] }, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.map((candidate) => (candidate.id === updated.id ? updated : candidate)),
      };
    });
  }

  /** Drops a removed row from every cached list this screen holds, and
   * shrinks each cached `meta.total` to match — the same partial-key reach
   * `writeRowToCache` already uses, applied to a removal instead of an
   * edit. */
  function removeRowFromCache(id: string) {
    queryClient.setQueriesData<Page<UserResource>>({ queryKey: ["users"] }, (existing) => {
      if (existing === undefined || !existing.data.some((candidate) => candidate.id === id)) {
        return existing;
      }
      return {
        ...existing,
        data: existing.data.filter((candidate) => candidate.id !== id),
        meta: { ...existing.meta, total: Math.max(0, existing.meta.total - 1) },
      };
    });
  }

  /**
   * Removing a user (Story 2.6 retrofit of the row action Story 2.2
   * deferred). Runs the existing, already-audited `DELETE /users/{id}`
   * (`app/api/users.py`'s `remove_user`) — this dialog adds a UI trigger
   * and a confirmation, nothing more; the last-Owner and protected-row
   * guards are exactly the ones that route already enforces, unchanged,
   * and a refusal renders inline in the dialog rather than silently
   * closing it (VC-2.6-13).
   */
  async function confirmRemove() {
    if (removeTarget === null) {
      return;
    }
    setIsRemoving(true);
    setRemoveError(undefined);
    try {
      await apiFetch<{ removed: boolean }>(`/users/${removeTarget.id}`, { method: "DELETE" });
      removeRowFromCache(removeTarget.id);
      setRemoveTarget(null);
    } catch (error) {
      setRemoveError(
        error instanceof ApiError ? error.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsRemoving(false);
    }
  }

  async function sendPatch(
    row: UserResource,
    column: DataTableColumn<UserResource>,
    payload: EditCommitPayload,
    expectedUpdatedAt: string,
  ): Promise<EditCommitOutcome> {
    try {
      const updated = await apiFetch<UserResource>(`/users/${row.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...patchBodyFor(column.key, payload),
          expected_updated_at: expectedUpdatedAt,
        }),
      });
      writeRowToCache(updated);
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError && error.code === "conflict") {
        const message = conflictMessage(
          {
            field: error.field,
            currentValue: error.currentValue,
            attemptedValue: payload.kind === "value" ? payload.value : undefined,
            fallbackMessage: error.message,
          },
          labelForConflictValue,
        );
        return {
          ok: false,
          conflict: true,
          message,
          ...(error.currentValue !== undefined ? { currentValue: error.currentValue } : {}),
        };
      }
      if (error instanceof ApiError) {
        return { ok: false, message: error.message };
      }
      throw error;
    }
  }

  async function commitEdit(args: InlineEditCommitArgs<UserResource>): Promise<EditCommitOutcome> {
    const expectedUpdatedAt = freshestKnownValue(latestUpdatedAtRef.current, args.row.id, args.row.updated_at);
    return sendPatch(args.row, args.column, args.payload, expectedUpdatedAt);
  }

  /**
   * "Keep mine" and "Keep theirs" (judgement call 4) both start the same
   * way — a genuine re-fetch of this row, not a re-read of the cache that
   * caused the conflict in the first place. `fetchQuery` against the exact
   * key `listQuery` itself subscribes to, so the refreshed page also
   * updates whatever `DataTable` is currently rendering, whichever
   * resolution the account holder picks.
   */
  async function resolveConflictEdit(args: InlineEditConflictArgs<UserResource>): Promise<EditCommitOutcome> {
    const fresh = await queryClient.fetchQuery({
      queryKey: listQueryKey,
      queryFn: () => apiFetch<Page<UserResource>>(`/users?${search}`),
    });
    const freshRow = fresh.data.find((candidate) => candidate.id === args.row.id);
    // The re-fetch above is more authoritative than anything `commitEdit`
    // would otherwise fall back to — recorded here so an edit fired
    // immediately after this resolution (before any re-render) still reads
    // it via `freshestKnownValue`, the same way a successful `sendPatch`
    // already does through `writeRowToCache`.
    if (freshRow !== undefined) {
      latestUpdatedAtRef.current[freshRow.id] = freshRow.updated_at;
    }
    if (args.resolution === "theirs") {
      return { ok: true };
    }
    if (freshRow === undefined) {
      return { ok: false, message: "That person could not be found. They may have already been removed." };
    }
    return sendPatch(freshRow, args.column, { kind: "value", value: args.myValue }, freshRow.updated_at);
  }

  const columns = useMemo<DataTableColumn<UserResource>[]>(() => {
    const roleOptions = enumsQuery.data?.roles.map((role) => ({ value: role, label: ROLE_LABELS[role] })) ?? [];
    const definitions: DataTableColumn<UserResource>[] = [
      { key: "display_name", edit: { kind: "text" } },
      { key: "role", render: (row) => ROLE_LABELS[row.role] },
      { key: "status", render: (row) => <StatusBadge status={row.status} /> },
      { key: "email" },
      { key: "created_at", tabularFigures: true, render: (row) => formatDateCell(row.created_at) },
    ];
    // The fixed-option inline edit (spec judgement call 5) only attaches
    // once the real option set has actually loaded from `GET /meta/enums`
    // (AD-2) — the same "no component defines its own status/role list"
    // rule the filter row above already follows; a column with an empty
    // `<select>` would be worse than a moment of read-only.
    if (roleOptions.length > 0) {
      definitions[1] = { key: "role", render: (row) => ROLE_LABELS[row.role], edit: { kind: "select", options: roleOptions } };
    }
    return definitions;
  }, [enumsQuery.data]);

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 ref={headingRef} tabIndex={-1} className="text-title font-semibold tracking-title text-ink md:hidden">
            Users
          </h1>
          <p className="mt-2 text-body text-ink-secondary">Everyone who can sign in to PropDesk.</p>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {isOwner ? (
            <Button type="button" onClick={() => setCreateOpen(true)} className="w-full md:w-auto">
              Add user
            </Button>
          ) : null}
        </div>
      </div>
      <SavedViewChips listKey={USERS_LIST_KEY} />
      <DataTable
        caption="Users"
        itemNamePlural="users"
        tint="violet"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        filters={filters}
        {...(unfilteredTotalQuery.data ? { unfilteredTotal: unfilteredTotalQuery.data.meta.total } : {})}
        getRowHref={(row) => `/settings/users/${row.id}`}
        emptyState={{
          heading: "No users yet.",
          body: "Every account is signed in through this list — an Owner is always created first, so this state does not occur in practice.",
        }}
        onCommitEdit={commitEdit}
        onResolveConflict={resolveConflictEdit}
        {...(isReadOnly
          ? {}
          : {
              rowActions: (row: UserResource) => (
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`Remove ${row.display_name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setRemoveTarget(row);
                  }}
                >
                  Remove
                </Button>
              ),
            })}
      />
      {isOwner ? (
        <AddUserDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/settings/users/${id}`)}
        />
      ) : null}
      {removeTarget ? (
        <ConfirmDialog
          open={removeTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setRemoveTarget(null);
              setRemoveError(undefined);
            }
          }}
          title="Remove this person?"
          description={`Removing ${removeTarget.display_name} takes away their access right away and ends any sessions they're signed in to. Their account can be restored for 30 days.`}
          confirmLabel={`Remove ${removeTarget.display_name}`}
          confirmingLabel="Removing…"
          isConfirming={isRemoving}
          onConfirm={() => void confirmRemove()}
          fallbackFocusRef={headingRef}
          {...(removeError ? { errorMessage: removeError } : {})}
        />
      ) : null}
    </div>
  );
}
